import { Component, ElementRef, HostListener, inject, signal } from '@angular/core';
import { InputGLobal } from '../utils/input-global';
import { ResolvedInputOption } from '../utils/input.types';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'lib-ui-input-select',
  templateUrl: './ui-input-select.component.html',
  styleUrl: './ui-input-select.component.scss',
})
export class UiInputSelectComponent extends InputGLobal {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly isOpen = signal(false);
  readonly visibleCount = signal(10);

  get resolvedOptions(): ResolvedInputOption[] {
    return (this.valueOptions ?? []).map((option) => ({
      ...option,
      resolvedLabel:
        option.label?.trim() ||
        option.legend?.trim() ||
        String(option.value),
    }));
  }

  get visibleOptions(): ResolvedInputOption[] {
    return this.resolvedOptions.slice(0, this.visibleCount());
  }

  get selectedOption(): ResolvedInputOption | null {
    const currentValue = this.formControl?.value;
    return (
      this.resolvedOptions.find((option) => option.value === currentValue) ??
      null
    );
  }

  get displayValue(): string {
    return this.selectedOption?.resolvedLabel || this.placeholder || 'Selecione';
  }

  get hasValue(): boolean {
    return this.selectedOption !== null;
  }

  override get formControl(): FormControl<string | number | null> | null {
    return super.formControl as FormControl<string | number | null> | null;
  }

  togglePanel(): void {
    if (this.disabled) return;

    if (this.isOpen()) {
      this.closePanel();
      return;
    }

    this.openPanel();
  }

  openPanel(): void {
    if (this.disabled) return;

    this.visibleCount.set(10);
    this.isOpen.set(true);
  }

  closePanel(): void {
    this.isOpen.set(false);
  }

  selectOption(option: ResolvedInputOption): void {
    if (this.disabled || option.disabled) return;

    if (this.formControl) {
      this.formControl.setValue(option.value);
      this.formControl.markAsDirty();
      this.formControl.markAsTouched();
      this.formControl.updateValueAndValidity();
    }

    this.setValue(option.value);
    this.closePanel();
  }

  clearSelection(): void {
    if (this.disabled) return;

    if (this.formControl) {
      this.formControl.setValue(null);
      this.formControl.markAsDirty();
      this.formControl.markAsTouched();
      this.formControl.updateValueAndValidity();
    }

    this.setValue(null);
  }

  onPanelScroll(event: Event): void {
    const panel = event.target as HTMLElement;
    const nearBottom =
      panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 16;

    if (!nearBottom) return;

    if (this.visibleCount() >= this.resolvedOptions.length) return;

    this.visibleCount.update((count) => count + 10);
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.togglePanel();
      return;
    }

    if (event.key === 'Escape') {
      this.closePanel();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;

    if (!target) return;

    if (!this.elementRef.nativeElement.contains(target)) {
      this.closePanel();
    }
  }
}
